import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

/** Material UI Packages. */
import { Box } from '@mui/material';

/** Material UI Icons. */
import LinkIcon from '@mui/icons-material/Link';

/** Date Format Library. */
import dayjs from 'dayjs';

/** Internal Components. */
import InputField, { DateField } from 'shared/ui/InputForm';
import {
    AutocompleteField_Single,
    AutocompleteFieldTradePartner,
    AutocompleteField_String,
    AutocompleteField,
    AutocompleteField_CodeName,
} from 'shared/ui/AutoCompleteForm';
import GoogleLocation from 'shared/ui/GoogleLocation';
import { readSocketData } from 'shared/lib/fetchSocket';
import { normalizeTeamOptions, pickableTeamOptions, toTeamOption } from 'shared/lib/teamOptions';
import { handleRequiredState } from 'shared/lib/handleGoogle';
import { incotermList, trucking_type } from 'shared/config/constantsData';

import NewShipmentHouse from './NewShipmentHouse';
import NewShipmentContainer from './NewShipmentContainer';
import { actionMap } from './ActionMap';

/** Mapping of options lists for incoterms, trucking type.  */
const OPTIONS_MAP = {
    incotermList,
    trucking_type,
};

const FIELD_WIDTH = '190px';
const CONTAINER_FIELD_WIDTH = '230px';

/** Field renderer components */
export const FieldRenderer = {
    InputField: ({ field, commonProps, handleFormChange }) => (
        <InputField
            key={field.key}
            {...commonProps}
            onChange={handleFormChange}
            inputStyle={{ width: FIELD_WIDTH }}
            icon={field.icon ? LinkIcon : ''}
            sx={field.sx}
            labelStyle={field.labelStyle}
            tooltip="Link"
        />
    ),

    AutocompleteFieldTradePartner: ({
        field,
        commonProps,
        handleAutocompleteChange_obj,
        handleOptionsChange,
        autocompleteOptions,
    }) => (
        <AutocompleteFieldTradePartner
            key={field.key}
            {...commonProps}
            onChange={handleAutocompleteChange_obj(field.key)}
            onInputChange={(e, val) => handleOptionsChange(field.key, e, val, field.dataType)}
            onFocus={(e) => handleOptionsChange(field.key, e, '', field.dataType)}
            sx={{ gap: '15px' }}
            options={autocompleteOptions[field.key]?.options || []}
            AutocompleteStyle={{ width: FIELD_WIDTH }}
            disable
        />
    ),

    AutocompleteField_CodeName: ({
        field,
        commonProps,
        handleAutocompleteChange_obj,
        handleOptionsChange,
        autocompleteOptions,
    }) => (
        <AutocompleteField_CodeName
            key={field.key}
            {...commonProps}
            onChange={handleAutocompleteChange_obj(field.key)}
            onInputChange={(e, val) => handleOptionsChange(field.key, e, val, field.dataType)}
            onFocus={(e) => handleOptionsChange(field.key, e, '', field.dataType)}
            options={autocompleteOptions[field.key]?.options || []}
            AutocompleteStyle={{ width: FIELD_WIDTH }}
            disable
            displayKey={field.displayKey}
        />
    ),

    AutocompleteField: ({ field, commonProps, handleAutocompleteChange_string }) => (
        <AutocompleteField
            key={field.key}
            {...commonProps}
            onChange={(event, value) => handleAutocompleteChange_string(field.key, event, value)}
            options={[{ id: 1, content: 'sample' }]}
            disable={true}
            AutocompleteStyle={{ width: FIELD_WIDTH }}
        />
    ),

    AutocompleteField_String: ({
        field,
        commonProps,
        handleAutocompleteChange_string,
        handleOptionsChange,
        autocompleteOptions,
    }) => (
        <AutocompleteField_String
            key={field.key}
            {...commonProps}
            onChange={handleAutocompleteChange_string(field.key)}
            onInputChange={(e, val) => handleOptionsChange(field.key, e, val, field.dataType)}
            onFocus={(e) => handleOptionsChange(field.key, e, '', field.dataType)}
            AutocompleteStyle={{ width: FIELD_WIDTH }}
            options={autocompleteOptions[field.key]?.options || []}
            disable={false}
            sx={field.sx}
            freeSolo={field.freeSolo}
        />
    ),

    /** Team picker (OS-566): options are read from `il_team` when the field mounts — like the Client
     *  field reads its options on focus — and the form stores the picked `{ id, name }` like Client. */
    AutocompleteFieldTeam: ({ field, commonProps, handleAutocompleteChange_obj }) => {
        const dispatch = useDispatch();
        const [teams, setTeams] = useState([]);
        useEffect(() => {
            let active = true;
            readSocketData(dispatch, null, 'row', 'il_team', null, null, (received) => {
                if (active) setTeams(normalizeTeamOptions(received));
            });
            return () => {
                active = false;
            };
        }, [dispatch]);
        const { error, value, ...rest } = commonProps;
        return (
            <AutocompleteField_Single
                key={field.key}
                {...rest}
                value={value?.id ? value : null}
                onChange={(event, option) => handleAutocompleteChange_obj(field.key)(event, toTeamOption(option) ?? '')}
                options={pickableTeamOptions(teams, value)}
                getOptionLabel={(option) => option?.name || ''}
                isOptionEqualToValue={(option, selected) => option?.id === selected?.id}
                isError={error}
                width={FIELD_WIDTH}
                sx={field.sx}
                labelStyle={field.labelStyle}
            />
        );
    },

    AutocompleteField_Single: ({ field, commonProps, handleAutocompleteChange_obj }) => (
        <AutocompleteField_Single
            key={field.key}
            {...commonProps}
            onChange={handleAutocompleteChange_obj(field.key)}
            options={OPTIONS_MAP[field.options]}
            width={FIELD_WIDTH}
            sx={field.sx}
            labelStyle={field.labelStyle}
        />
    ),

    DateField: ({ field, formData, handleDateChange }) => (
        <DateField
            key={field.key}
            label={field.label}
            value={formData[field.key] ? dayjs(formData[field.key]) : null}
            onChange={(date) => handleDateChange(date, field.key)}
            width={FIELD_WIDTH}
        />
    ),

    NewShipmentHBL: ({ field, dialogConfig, setGridEditingKeys, gridApiRef, tabTitles, setTabTitles }) => (
        <NewShipmentHouse
            key={field.key}
            dialogConfig={dialogConfig}
            setGridEditingKeys={setGridEditingKeys}
            gridApiRef={gridApiRef}
            tabTitles={tabTitles}
            setTabTitles={setTabTitles}
        />
    ),

    TruckingNewShipmentAlias: ({ field, dialogConfig, setGridEditingKeys, gridApiRef }) => (
        <NewShipmentContainer
            key={field.key}
            dialogConfig={dialogConfig}
            setGridEditingKeys={setGridEditingKeys}
            gridApiRef={gridApiRef}
        />
    ),

    GoogleLocation: ({ field, commonProps, dispatch, dialogConfig }) => (
        <GoogleLocation
            key={field.key}
            {...commonProps}
            handleRequiredState={(newValue) =>
                handleRequiredState(newValue, {
                    keyname: field.key,
                    dispatch,
                    formAction: actionMap[dialogConfig.setFormDataAction],
                })
            }
        />
    ),
    // Container Autocomplete Fields (Shipper, Consignee on first row; Reference below)
    ContainerAutocompleteFields: ({
        field,
        dialogConfig,
        hblRow,
        handleAutocompleteChange_obj,
        handleOptionsChange,
        autocompleteOptions,
    }) => {
        const allFields = dialogConfig.sections
            .filter((item) => item.usage === 'NewShipmentCNTR')
            .flatMap((section) => section.sections);

        const partnerFields = allFields.filter(
            (f) => f.key === 'il_trade_partner_id_shipper' || f.key === 'il_trade_partner_id_consignee',
        );
        const referenceField = allFields.find((f) => f.key === 'reference');

        return (
            <Box key={field.key} sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
                    {partnerFields.map((containerField) => (
                        <AutocompleteFieldTradePartner
                            key={containerField.key}
                            label={containerField.label}
                            name={containerField.key}
                            value={hblRow[containerField.key]}
                            onChange={handleAutocompleteChange_obj(containerField.key)}
                            onInputChange={(e, val) =>
                                handleOptionsChange(containerField.key, e, val, containerField.dataType)
                            }
                            onFocus={(e) => handleOptionsChange(containerField.key, e, '', containerField.dataType)}
                            sx={{ gap: '0px' }}
                            options={autocompleteOptions[containerField.key]?.options || []}
                            AutocompleteStyle={{ width: CONTAINER_FIELD_WIDTH }}
                            disable
                        />
                    ))}
                </Box>
                {referenceField && (
                    <Box sx={{ display: 'flex', justifyContent: 'left', ml: 2 }}>
                        <InputField
                            key={referenceField.key}
                            label={referenceField.label}
                            name={referenceField.key}
                            value={hblRow[referenceField.key] ?? ''}
                            onChange={(e) => handleAutocompleteChange_obj(referenceField.key)(e, e.target.value)}
                            inputStyle={{ width: CONTAINER_FIELD_WIDTH }}
                            sx={{ ...referenceField.sx, gap: '0px' }}
                        />
                    </Box>
                )}
            </Box>
        );
    },
};
